import React from 'react';
import { useTranslation } from 'react-i18next';
import type { StructuredResults, StructuredResultItem } from './types';
import { WeatherForecastCard } from './components/Weather';
import { ResultItemRendererFactory } from './renderers';

export const StructuredResultsRenderer: React.FC<{ data: StructuredResults }> = ({ data }) => {
  const { t } = useTranslation();

  // SMART GROUPING IN FRONTEND - not LLM!
  const groupItemsByType = (items: StructuredResultItem[]) => {
    const groups: { [key: string]: StructuredResultItem[] } = {};
    
    items.forEach(item => {
      const type = item.content_type;
      if (!groups[type]) {
        groups[type] = [];
      }
      groups[type].push(item);
    });
    
    return groups;
  };

  const getSectionTitle = (contentType: string, count: number) => {
    const titleKeys: Record<string, string> = {
      'bike_trail': 'results.sections.bikeTrails',
      'hiking_trail': 'results.sections.hikingTrails', 
      'attraction': 'results.sections.attractions',
      'event': 'results.sections.events'
    };
    
    const translatedTitle = titleKeys[contentType] 
      ? t(titleKeys[contentType]) 
      : contentType;
    
    return `${translatedTitle} (${count})`;
  };

  const groupedItems = groupItemsByType(data.items || []);

  return (
    <div className="space-y-3">
      {/* Introduction - dynamic from backend */}
      {data.header_text && (
        <div className="text-sm md:text-base text-gray-800 leading-relaxed">
          {data.header_text}
        </div>
      )}
      
      {/* Weather Forecast Section */}
      {data.weather_forecast && (
        <WeatherForecastCard weather={data.weather_forecast} />
      )}
      
      {/* Grouped Content Sections - FRONTEND HANDLES THIS */}
      {Object.entries(groupedItems).map(([contentType, items]) => (
        <div key={contentType}>
          <h4 className="text-md font-semibold text-gray-700 mb-2">
            {getSectionTitle(contentType, items.length)}
          </h4>
          
          <div className="space-y-2">
            {items.map((item, itemIndex) => (
              <ResultItemRendererFactory key={itemIndex} item={item} />
            ))}
          </div>
        </div>
      ))}
      
      {/* Footer question - dynamic from backend, styled like SummaryRenderer */}
      {data.footer_text && (
        <div className="text-sm md:text-base text-gray-800 font-semibold leading-relaxed mt-4 pt-3 border-t border-gray-200">
          {data.footer_text}
        </div>
      )}
    </div>
  );
};
